import { motion } from "framer-motion";
import { ArrowRight, BatteryCharging, ChevronDown, MapPin, ShieldCheck } from "lucide-react";
import { publicAsset } from "../data/config";
import { AnimatedCounter } from "./AnimatedCounter";

const HERO_BG = publicAsset("scooters/enhanced/hero-showroom.jpg");

const PROOF_ICONS = { BatteryCharging, ShieldCheck, MapPin };

const renderHeroTitleLine = (line) => {
  const normalized = line.replace(/EVLuxe|EvLuxe/g, "evLUXE");
  if (!normalized.includes("evLUXE")) return normalized;
  const [before, after] = normalized.split("evLUXE");
  return (
    <>
      {before}
      <span className="brand-wordmark-text">evLUXE</span>
      {after}
    </>
  );
};

export const Hero = ({ content, onScrollTo }) => {
  const hero = content.hero;
  const actions = content.actions;
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[78svh] sm:min-h-[88svh] flex items-center overflow-hidden pt-[5.5rem] pb-10 sm:pt-28 sm:pb-20"
    >
      <img
        src={HERO_BG}
        alt={hero.imageAlt}
        className="absolute inset-0 h-full w-full object-cover"
        fetchPriority="high"
      />
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(10,10,12,0.98)_0%,rgba(10,10,12,0.82)_38%,rgba(10,10,12,0.38)_74%,rgba(10,10,12,0.72)_100%)]" aria-hidden="true" />
      <div className="absolute inset-0 grid-bg opacity-35" aria-hidden="true" />
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-surface-deep to-transparent" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-10 w-full">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.75, ease: "easeOut" }}
          className="max-w-3xl"
        >
          <span className="section-kicker mb-4 sm:mb-6">{hero.kicker}</span>

          <h1 className="font-display font-extrabold leading-[1.03] text-[2.45rem] sm:text-5xl lg:text-7xl">
            <span className="block">{renderHeroTitleLine(hero.titleLine1)}</span>
            <span className="block">{hero.titleLine2}</span>
            <span className="block text-brand glow-text">{hero.titleAccent}</span>
          </h1>

          <p className="mt-4 sm:mt-6 text-white/72 text-[0.98rem] sm:text-lg max-w-2xl leading-relaxed">
            {hero.description}
          </p>

          <div className="mt-6 sm:mt-8 grid grid-cols-2 gap-3 sm:flex sm:flex-wrap">
            <button
              onClick={() => onScrollTo("models")}
              data-testid="hero-explore-cta"
              className="btn-glow inline-flex items-center justify-center gap-2 bg-brand text-black font-semibold px-4 sm:px-6 py-3 rounded-full text-sm sm:text-base"
            >
              {actions.viewModels} <ArrowRight size={18} />
            </button>
            <button
              onClick={() => onScrollTo("register")}
              data-testid="hero-register-cta"
              className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-brand/70 text-white px-4 sm:px-6 py-3 rounded-full transition-colors text-sm sm:text-base"
            >
              {actions.registerInterest}
            </button>
          </div>

          <div className="mt-5 sm:mt-9 grid gap-2.5 sm:gap-3 sm:grid-cols-3 max-w-3xl">
            {hero.proofPoints.map((point) => {
              const Icon = PROOF_ICONS[point.icon] || ShieldCheck;
              return (
                <div key={point.label} className="inline-flex items-center gap-2 text-[0.8rem] sm:text-sm text-white/72">
                  <Icon size={17} className="text-brand shrink-0" />
                  <span>{point.label}</span>
                </div>
              );
            })}
          </div>

          <div className="mt-6 sm:mt-10 grid grid-cols-4 gap-2 sm:gap-4 max-w-3xl">
            {hero.stats.map((s) => (
              <div key={s.label} className="min-w-0 border-l border-brand/45 pl-2 sm:pl-4">
                <div className={`font-display font-bold text-brand leading-tight ${s.display ? "text-[0.68rem] sm:text-xl" : "text-lg sm:text-3xl"}`}>
                  <AnimatedCounter value={s.value} suffix={s.suffix} display={s.display} />
                </div>
                <div className="text-[0.62rem] sm:text-xs text-white/58 mt-1 leading-tight">{s.label}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      <button
        onClick={() => onScrollTo("models")}
        className="hidden sm:flex absolute bottom-5 left-1/2 -translate-x-1/2 text-white/55 hover:text-brand transition-colors flex-col items-center gap-1"
        aria-label="Scroll to models"
        data-testid="scroll-indicator"
      >
        <span className="text-[10px] font-mono">{hero.scrollLabel}</span>
        <ChevronDown className="animate-bounce" size={20} />
      </button>
    </section>
  );
};

export default Hero;
